<template>
  <div class="space-y-6">
    <!-- Header -->
    <div>
      <h2 class="text-xl font-semibold mb-2">Översikt för {{ currentMonthName }}</h2>
      <p class="text-gray-600">Affärsmöjligheter och försäljningsstatistik</p>
    </div>

    <!-- Metric Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600">Affärsmöjligheter</p>
            <p class="text-3xl font-semibold mt-2">{{ formatNumber(metrics.totalOpportunities) }}</p>
          </div>
          <div class="w-12 h-12 rounded-full flex items-center justify-center" style="background-color: #E5FEF5">
            <BaseIcon icon="file-alt" class="text-lg" style="color: #193B2D" />
          </div>
        </div>
      </div>
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600">Totalt ordervärde</p>
            <p class="text-3xl font-semibold mt-2">{{ formatNumber(metrics.totalOrderValue) }} kr</p>
          </div>
          <div class="w-12 h-12 rounded-full flex items-center justify-center" style="background-color: #193B2D">
            <BaseIcon icon="dollar-sign" class="text-lg" style="color: #E5FEF5" />
          </div>
        </div>
      </div>
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600">Aktiva säljare</p>
            <p class="text-3xl font-semibold mt-2">{{ formatNumber(metrics.uniqueSellers) }}</p>
          </div>
          <div class="w-12 h-12 rounded-full flex items-center justify-center" style="background-color: #FFA500">
            <BaseIcon icon="users" class="text-lg text-white" />
          </div>
        </div>
      </div>
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600">Snitt ordervärde</p>
            <p class="text-3xl font-semibold mt-2">{{ formatNumber(metrics.averageOrderValue) }} kr</p>
          </div>
          <div class="w-12 h-12 rounded-full flex items-center justify-center" style="background-color: #87CEEB">
            <BaseIcon icon="chart-line" class="text-lg" style="color: #193B2D" />
          </div>
        </div>
      </div>
    </div>

    <!-- Muntlig överenskommelse Opportunities -->
    <div v-if="muntligOpportunities.length > 0" class="rounded-lg shadow p-6" style="background-color: #CFD5DF">
      <div class="mb-4">
        <h3 class="text-lg mb-2">Muntlig överenskommelse - {{ currentMonthName }}</h3>
        <p class="text-sm text-gray-600">
          {{ muntligOpportunities.length }} affärsmöjligheter i fasen Muntlig överenskommelse med avslutsdatum i {{ currentMonthName }} &bull;
          Totalt värde: {{ formatNumber(muntligTotalValue) }} kr
        </p>
      </div>
      <div class="overflow-auto bg-white rounded-lg">
        <table class="w-full">
          <thead>
            <tr class="border-b bg-gray-50">
              <th class="text-left p-3 w-12"></th>
              <th class="text-left p-3">#</th>
              <th class="text-left p-3">Säljare</th>
              <th class="text-left p-3">Företag</th>
              <th class="text-left p-3">Avslutsdatum</th>
              <th class="text-right p-3">Värde</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(opp, index) in muntligOpportunities" :key="'muntlig-' + index" class="border-b hover:bg-gray-50" :class="{ 'opacity-50': excludedMuntligOpportunities.has(index) }">
              <td class="p-3">
                <input type="checkbox" :checked="!excludedMuntligOpportunities.has(index)" @change="toggleMuntligExclusion(index)" class="w-4 h-4 cursor-pointer" />
              </td>
              <td class="p-3">
                <span class="text-gray-700 font-medium">{{ index + 1 }}</span>
              </td>
              <td class="p-3 font-medium">{{ opp['Säljare'] || 'Okänd' }}</td>
              <td class="p-3">{{ opp['Företag'] || opp['Order: Företag'] || opp['col_3'] || 'Okänt' }}</td>
              <td class="p-3">{{ opp['Avslutsdatum'] ? new Date(opp['Avslutsdatum']).toLocaleDateString('sv-SE', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }) : '-' }}</td>
              <td class="text-right p-3 font-bold" style="color: #0F2C5D">{{ formatNumber(Number(opp['Ordervärde']) || 0) }} kr</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="border-t-2 bg-gray-50">
              <td colspan="5" class="p-3 font-bold text-right">
                Totalt värde ({{ muntligOpportunities.length - excludedMuntligOpportunities.size }} av {{ muntligOpportunities.length }} affärsmöjligheter):
              </td>
              <td class="text-right p-3 font-bold text-lg" style="color: #0F2C5D">{{ formatNumber(muntligTotalValue) }} kr</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Total Pipeline per Fas - Full Width -->
    <div class="bg-white rounded-lg shadow p-6">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h3 class="text-lg font-medium mb-1">Total pipeline per fas</h3>
          <p class="text-sm text-gray-600">Klicka på en fas för att se alla affärsmöjligheter</p>
        </div>
        <div class="flex items-center gap-2">
          <label class="text-sm text-gray-600 whitespace-nowrap">Filtrera på säljare:</label>
          <select v-model="phaseFilterSeller" class="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white min-w-[200px]">
            <option value="">Alla säljare</option>
            <option v-for="seller in uniqueSellers" :key="seller" :value="seller">{{ seller }}</option>
          </select>
        </div>
      </div>
      <div v-if="filteredPhaseData.length > 0">
        <canvas ref="phaseTotalChart" height="160"></canvas>
      </div>
      <div v-else class="flex items-center justify-center h-64 text-gray-500">Ingen data för innevarande månad</div>
    </div>

    <!-- Charts Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Seller Performance by Phase -->
      <div class="bg-white rounded-lg shadow p-6">
        <h3 class="text-lg font-medium mb-4">Ordervärde per säljare (per fas)</h3>
        <div v-if="sellerData.length > 0">
          <canvas ref="sellerValueChart" height="250"></canvas>
          <p class="text-sm text-gray-600 text-center mt-2">Klicka på en säljare för att se detaljer</p>
        </div>
        <div v-else class="flex items-center justify-center h-64 text-gray-500">Ingen data för innevarande månad</div>
      </div>

      <!-- Opportunities per Seller -->
      <div class="bg-white rounded-lg shadow p-6">
        <h3 class="text-lg font-medium mb-4">Antal affärsmöjligheter per säljare (per fas)</h3>
        <div v-if="sellerOpportunityCount.length > 0">
          <canvas ref="sellerCountChart" height="250"></canvas>
        </div>
        <div v-else class="flex items-center justify-center h-64 text-gray-500">Ingen data för innevarande månad</div>
      </div>

      <!-- Average Order Value -->
      <div class="bg-white rounded-lg shadow p-6">
        <h3 class="text-lg font-medium mb-4">Genomsnittligt ordervärde per säljare</h3>
        <div v-if="sellerAverageValue.length > 0">
          <canvas ref="averageValueChart" height="200"></canvas>
        </div>
        <div v-else class="flex items-center justify-center h-64 text-gray-500">Ingen data för innevarande månad</div>
      </div>
    </div>

    <!-- Closing Timeline -->
    <div v-if="closingByDay.length > 0" class="bg-white rounded-lg shadow p-6">
      <h3 class="text-lg font-medium mb-4">Affärsmöjligheter att stänga resten av månaden</h3>
      <canvas ref="closingDayChart" height="150"></canvas>
      <div class="mt-4 text-sm text-gray-600">
        Totalt värde att stänga: {{ formatNumber(closingByDayTotal) }} kr
      </div>
    </div>

    <!-- Monthly Forecast -->
    <div v-if="closingByMonth.length > 0" class="bg-white rounded-lg shadow p-6">
      <h3 class="text-lg font-medium mb-4">Affärsmöjligheter per månad (innevarande + 12 månader framåt)</h3>
      <canvas ref="closingMonthChart" height="150"></canvas>
      <div class="mt-4 grid grid-cols-2 gap-4 text-sm text-gray-600">
        <div>
          <span class="font-semibold">Totalt värde (13 månader):</span>
          {{ formatNumber(closingByMonth.reduce((sum, m) => sum + m.totalValue, 0)) }} kr
        </div>
        <div>
          <span class="font-semibold">Totalt antal (13 månader):</span>
          {{ formatNumber(closingByMonth.reduce((sum, m) => sum + m.antal, 0)) }} st
        </div>
      </div>
    </div>

    <!-- Top 10 Opportunities Table -->
    <div v-if="top10Opportunities.length > 0" class="bg-white rounded-lg shadow p-6">
      <div class="mb-4 flex items-start justify-between gap-4">
        <div class="flex-1">
          <h3 class="text-lg font-semibold mb-2">Topp 10 största affärsmöjligheterna - {{ currentMonthName }}</h3>
          <p class="text-sm text-gray-600">
            De 10 största affärsmöjligheterna av totalt {{ metrics.totalOpportunities }} {{ metrics.totalOpportunities === 1 ? 'möjlighet' : 'möjligheter' }} &bull;
            Topp 10 utgör {{ top10Percentage.toFixed(1) }}% av totalt värde ({{ formatNumber(top10Value) }} kr av {{ formatNumber(top10BaseValue) }} kr)
          </p>
        </div>
        <div class="flex items-center gap-2">
          <label class="text-sm text-gray-600 whitespace-nowrap">Filtrera på fas:</label>
          <select v-model="top10PhaseFilter" class="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px] bg-white">
            <option value="Alla">Alla faser</option>
            <option v-for="phase in phaseOrder" :key="phase" :value="phase">{{ phase }}</option>
          </select>
        </div>
      </div>
      <div class="overflow-auto">
        <table class="w-full">
          <thead>
            <tr class="border-b">
              <th class="text-left p-3">#</th>
              <th class="text-left p-3">Säljare</th>
              <th class="text-left p-3">Företag</th>
              <th class="text-left p-3">Avslutsdatum</th>
              <th class="text-left p-3">Fas</th>
              <th class="text-right p-3">Värde</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(opp, index) in top10Opportunities" :key="index" class="border-b hover:bg-gray-50" :class="index < 3 ? 'bg-yellow-50' : ''">
              <td class="p-3">
                <div class="flex items-center gap-2">
                  <span v-if="index === 0">🥇</span>
                  <span v-else-if="index === 1">🥈</span>
                  <span v-else-if="index === 2">🥉</span>
                  <span v-else class="text-gray-500 font-medium w-8 text-center">{{ index + 1 }}</span>
                </div>
              </td>
              <td class="p-3 font-medium">{{ opp['Säljare'] || 'Okänd' }}</td>
              <td class="p-3">{{ opp['Företag'] || opp['Order: Företag'] || opp['col_3'] || 'Okänt' }}</td>
              <td class="p-3">{{ opp['Avslutsdatum'] ? new Date(opp['Avslutsdatum']).toLocaleDateString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric' }) : '-' }}</td>
              <td class="p-3">
                <span class="inline-block px-2 py-1 rounded text-white text-sm font-medium" :style="{ backgroundColor: phaseColor(normalizePhase(opp['Fas'])) }">
                  {{ normalizePhase(opp['Fas']) }}
                </span>
              </td>
              <td class="text-right p-3 font-bold" style="color: #0F2C5D">{{ formatNumber(Number(opp['Ordervärde']) || 0) }} kr</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="border-t-2 bg-gray-50">
              <td colspan="5" class="p-3 font-bold text-right">Topp 10 totalt ({{ top10Percentage.toFixed(1) }}% av totalt värde):</td>
              <td class="text-right p-3 font-bold text-lg" style="color: #0F2C5D">{{ formatNumber(top10Value) }} kr</td>
            </tr>
            <tr class="bg-gray-100">
              <td colspan="5" class="p-3 font-bold text-right">{{ top10PhaseFilter !== 'Alla' ? 'Totalt i vald fas:' : 'Totalt alla affärsmöjligheter:' }}</td>
              <td class="text-right p-3 font-bold text-gray-700 text-lg">{{ formatNumber(top10BaseValue) }} kr</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Seller Details Modal -->
    <modal name="seller-detail-modal" :width="900" height="auto" :scrollable="true" :adaptive="true">
      <div class="p-6" v-if="selectedSeller">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold">Affärsmöjligheter för {{ selectedSeller }}</h3>
          <button class="text-gray-400 hover:text-gray-600" @click="$modal.hide('seller-detail-modal')">
            <BaseIcon icon="times" />
          </button>
        </div>
        <p class="text-sm text-gray-600 mb-4">{{ selectedSellerOpportunities.length }} affärsmöjligheter i innevarande månad</p>

        <!-- Summary Cards -->
        <div class="grid grid-cols-3 gap-4 mb-6">
          <div class="bg-gray-50 rounded-lg p-4">
            <p class="text-sm text-gray-600">Totalt antal</p>
            <p class="text-2xl font-semibold mt-1">{{ formatNumber(selectedSellerOpportunities.length) }}</p>
          </div>
          <div class="bg-gray-50 rounded-lg p-4">
            <p class="text-sm text-gray-600">Totalt värde</p>
            <p class="text-2xl font-semibold mt-1">{{ formatNumber(selectedSellerTotalValue) }} kr</p>
          </div>
          <div class="bg-gray-50 rounded-lg p-4">
            <p class="text-sm text-gray-600">Genomsnitt</p>
            <p class="text-2xl font-semibold mt-1">{{ formatNumber(selectedSellerAvgValue) }} kr</p>
          </div>
        </div>

        <!-- Table -->
        <div class="border rounded-lg overflow-auto">
          <table class="w-full">
            <thead class="bg-gray-50">
              <tr>
                <th class="text-left p-2 border-b whitespace-nowrap">#</th>
                <th class="text-left p-2 border-b whitespace-nowrap">Företag</th>
                <th class="text-left p-2 border-b whitespace-nowrap">Avslutsdatum</th>
                <th class="text-right p-2 border-b whitespace-nowrap">Ordervärde</th>
                <th class="text-left p-2 border-b whitespace-nowrap">Fas</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in selectedSellerOpportunities" :key="index" class="hover:bg-gray-50 border-b">
                <td class="p-2 whitespace-nowrap">{{ index + 1 }}</td>
                <td class="p-2 whitespace-nowrap">{{ row['Företag'] || row['Order: Företag'] || row['col_3'] || '-' }}</td>
                <td class="p-2 whitespace-nowrap">{{ row['Avslutsdatum'] ? new Date(row['Avslutsdatum']).toLocaleDateString('sv-SE') : '-' }}</td>
                <td class="text-right p-2 whitespace-nowrap">{{ formatNumber(Number(row['Ordervärde']) || 0) }} kr</td>
                <td class="p-2 whitespace-nowrap">{{ row['Fas'] || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </modal>

    <!-- Phase Details Modal -->
    <modal name="phase-detail-modal" :width="900" height="auto" :scrollable="true" :adaptive="true">
      <div class="p-6" v-if="selectedPhase">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold">
            Affärsmöjligheter för fasen {{ selectedPhase }}
            <span v-if="phaseFilterSeller"> - {{ phaseFilterSeller }}</span>
          </h3>
          <button class="text-gray-400 hover:text-gray-600" @click="$modal.hide('phase-detail-modal')">
            <BaseIcon icon="times" />
          </button>
        </div>
        <p class="text-sm text-gray-600 mb-4">
          {{ selectedPhaseOpportunities.length }} affärsmöjligheter i innevarande månad
          <span v-if="phaseFilterSeller"> för {{ phaseFilterSeller }}</span>
        </p>

        <!-- Summary Cards -->
        <div class="grid grid-cols-3 gap-4 mb-6">
          <div class="bg-gray-50 rounded-lg p-4">
            <p class="text-sm text-gray-600">Totalt antal</p>
            <p class="text-2xl font-semibold mt-1">{{ formatNumber(selectedPhaseOpportunities.length) }}</p>
          </div>
          <div class="bg-gray-50 rounded-lg p-4">
            <p class="text-sm text-gray-600">Totalt värde</p>
            <p class="text-2xl font-semibold mt-1">{{ formatNumber(selectedPhaseTotalValue) }} kr</p>
          </div>
          <div class="bg-gray-50 rounded-lg p-4">
            <p class="text-sm text-gray-600">Genomsnitt</p>
            <p class="text-2xl font-semibold mt-1">{{ formatNumber(selectedPhaseAvgValue) }} kr</p>
          </div>
        </div>

        <!-- Table -->
        <div class="border rounded-lg overflow-auto">
          <table class="w-full">
            <thead class="bg-gray-50">
              <tr>
                <th class="text-left p-2 border-b whitespace-nowrap">#</th>
                <th class="text-left p-2 border-b whitespace-nowrap">Säljare</th>
                <th class="text-left p-2 border-b whitespace-nowrap">Företag</th>
                <th class="text-left p-2 border-b whitespace-nowrap">Avslutsdatum</th>
                <th class="text-right p-2 border-b whitespace-nowrap">Ordervärde</th>
                <th class="text-left p-2 border-b whitespace-nowrap">Fas</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in selectedPhaseOpportunities" :key="index" class="hover:bg-gray-50 border-b">
                <td class="p-2 whitespace-nowrap">{{ index + 1 }}</td>
                <td class="p-2 whitespace-nowrap font-medium">{{ row['Säljare'] || 'Okänd' }}</td>
                <td class="p-2 whitespace-nowrap">{{ row['Företag'] || row['Order: Företag'] || row['col_3'] || '-' }}</td>
                <td class="p-2 whitespace-nowrap">{{ row['Avslutsdatum'] ? new Date(row['Avslutsdatum']).toLocaleDateString('sv-SE') : '-' }}</td>
                <td class="text-right p-2 whitespace-nowrap">{{ formatNumber(Number(row['Ordervärde']) || 0) }} kr</td>
                <td class="p-2 whitespace-nowrap">{{ row['Fas'] || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import Chart from "chart.js";
import "chartjs-plugin-annotation";

const PHASE_ORDER = ["Muntlig överenskommelse", "Offert / Andra möte", "Kvalificering", "Fallback"];
const PHASE_COLORS = {
  "Muntlig överenskommelse": "#0F2C5D",
  "Offert / Andra möte": "#4F8FE2",
  "Kvalificering": "#F3B44F",
  "Fallback": "#F7C788",
};

function normalizePhase(phase) {
  if (!phase) return "Okänd";
  const normalized = phase.trim();
  if (normalized.toLowerCase().includes("kvalificering")) return "Kvalificering";
  if (normalized.toLowerCase().includes("offert") || normalized.toLowerCase().includes("andra möte")) return "Offert / Andra möte";
  if (normalized.toLowerCase().includes("muntlig")) return "Muntlig överenskommelse";
  if (normalized.toLowerCase().includes("fallback") || normalized.toLowerCase().includes("övrigt")) return "Fallback";
  return normalized;
}

export default {
  name: "PipelineHome",
  props: {
    data: { type: Array, required: true },
  },
  data() {
    return {
      selectedSeller: null,
      selectedPhase: null,
      phaseFilterSeller: "",
      top10PhaseFilter: "Alla",
      excludedMuntligOpportunities: new Set(),
      charts: {},
      phaseOrder: PHASE_ORDER,
    };
  },
  computed: {
    currentMonthName() {
      return new Date().toLocaleDateString("sv-SE", { month: "long", year: "numeric" });
    },
    currentMonthData() {
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      return this.data.filter((row) => {
        const dateStr = row["Avslutsdatum"];
        if (!dateStr) return false;
        const date = new Date(dateStr);
        return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
      });
    },
    metrics() {
      const totalOpportunities = this.currentMonthData.length;
      const totalOrderValue = this.currentMonthData.reduce((sum, row) => sum + (Number(row["Ordervärde"]) || 0), 0);
      const uniqueSellers = new Set(this.currentMonthData.map((row) => row["Säljare"]).filter(Boolean)).size;
      const averageOrderValue = totalOpportunities > 0 ? totalOrderValue / totalOpportunities : 0;
      return { totalOpportunities, totalOrderValue, uniqueSellers, averageOrderValue };
    },
    sellerData() {
      const sellerMap = {};
      this.currentMonthData.forEach((row) => {
        const seller = row["Säljare"] || "Okänd";
        const phase = normalizePhase(row["Fas"]);
        const value = Number(row["Ordervärde"]) || 0;
        if (!sellerMap[seller]) sellerMap[seller] = {};
        sellerMap[seller][phase] = (sellerMap[seller][phase] || 0) + value;
      });
      return Object.entries(sellerMap)
        .map(([name, phases]) => {
          const total = Object.values(phases).reduce((sum, val) => sum + val, 0);
          return { name, ...phases, total };
        })
        .sort((a, b) => b.total - a.total);
    },
    sellerOpportunityCount() {
      const sellerMap = {};
      this.currentMonthData.forEach((row) => {
        const seller = row["Säljare"] || "Okänd";
        const phase = normalizePhase(row["Fas"]);
        if (!sellerMap[seller]) sellerMap[seller] = {};
        sellerMap[seller][phase] = (sellerMap[seller][phase] || 0) + 1;
      });
      return Object.entries(sellerMap)
        .map(([name, phases]) => {
          const total = Object.values(phases).reduce((sum, val) => sum + val, 0);
          return { name, ...phases, total };
        })
        .sort((a, b) => b.total - a.total);
    },
    sellerAverageValue() {
      const sellerMap = {};
      this.currentMonthData.forEach((row) => {
        const seller = row["Säljare"] || "Okänd";
        const value = Number(row["Ordervärde"]) || 0;
        if (!sellerMap[seller]) sellerMap[seller] = { total: 0, count: 0 };
        sellerMap[seller].total += value;
        sellerMap[seller].count += 1;
      });
      return Object.entries(sellerMap)
        .map(([name, d]) => ({ name, genomsnitt: d.count > 0 ? d.total / d.count : 0 }))
        .sort((a, b) => b.genomsnitt - a.genomsnitt);
    },
    phaseData() {
      const phaseMap = {};
      this.currentMonthData.forEach((row) => {
        const phase = normalizePhase(row["Fas"]);
        const value = Number(row["Ordervärde"]) || 0;
        if (!phaseMap[phase]) phaseMap[phase] = { value: 0, count: 0 };
        phaseMap[phase].value += value;
        phaseMap[phase].count += 1;
      });
      return Object.entries(phaseMap).map(([name, d]) => ({ name, value: d.value, count: d.count }));
    },
    uniqueSellers() {
      const sellers = new Set();
      this.currentMonthData.forEach((row) => {
        if (row["Säljare"]) sellers.add(row["Säljare"]);
      });
      return Array.from(sellers).sort();
    },
    filteredPhaseData() {
      const phaseMap = {};
      const filtered = this.phaseFilterSeller
        ? this.currentMonthData.filter((row) => row["Säljare"] === this.phaseFilterSeller)
        : this.currentMonthData;
      filtered.forEach((row) => {
        const phase = normalizePhase(row["Fas"]);
        const value = Number(row["Ordervärde"]) || 0;
        if (!phaseMap[phase]) phaseMap[phase] = { value: 0, count: 0 };
        phaseMap[phase].value += value;
        phaseMap[phase].count += 1;
      });
      return PHASE_ORDER.map((phase) => {
        const found = phaseMap[phase];
        return { name: phase, value: found ? found.value : 0, count: found ? found.count : 0 };
      });
    },
    top10Opportunities() {
      let filteredData = this.currentMonthData;
      if (this.top10PhaseFilter !== "Alla") {
        filteredData = filteredData.filter((row) => normalizePhase(row["Fas"]) === this.top10PhaseFilter);
      }
      return [...filteredData]
        .sort((a, b) => (Number(b["Ordervärde"]) || 0) - (Number(a["Ordervärde"]) || 0))
        .slice(0, 10);
    },
    top10Value() {
      return this.top10Opportunities.reduce((sum, opp) => sum + (Number(opp["Ordervärde"]) || 0), 0);
    },
    top10BaseValue() {
      if (this.top10PhaseFilter !== "Alla") {
        return this.currentMonthData
          .filter((row) => normalizePhase(row["Fas"]) === this.top10PhaseFilter)
          .reduce((sum, row) => sum + (Number(row["Ordervärde"]) || 0), 0);
      }
      return this.metrics.totalOrderValue;
    },
    top10Percentage() {
      return this.top10BaseValue > 0 ? (this.top10Value / this.top10BaseValue) * 100 : 0;
    },
    selectedPhaseOpportunities() {
      if (!this.selectedPhase) return [];
      let filtered = this.currentMonthData.filter((row) => normalizePhase(row["Fas"]) === this.selectedPhase);
      if (this.phaseFilterSeller) {
        filtered = filtered.filter((row) => row["Säljare"] === this.phaseFilterSeller);
      }
      return filtered.sort((a, b) => {
        const dateA = a["Avslutsdatum"] ? new Date(a["Avslutsdatum"]).getTime() : 0;
        const dateB = b["Avslutsdatum"] ? new Date(b["Avslutsdatum"]).getTime() : 0;
        return dateA - dateB;
      });
    },
    selectedPhaseTotalValue() {
      return this.selectedPhaseOpportunities.reduce((sum, row) => sum + (Number(row["Ordervärde"]) || 0), 0);
    },
    selectedPhaseAvgValue() {
      const len = this.selectedPhaseOpportunities.length;
      return len > 0 ? this.selectedPhaseTotalValue / len : 0;
    },
    orderedPhaseData() {
      return PHASE_ORDER
        .map((name) => this.phaseData.find((p) => p.name === name))
        .filter(Boolean);
    },
    muntligOpportunities() {
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      return this.data
        .filter((row) => {
          const dateStr = row["Avslutsdatum"];
          if (!dateStr) return false;
          const date = new Date(dateStr);
          if (date.getMonth() !== currentMonth || date.getFullYear() !== currentYear) return false;
          return normalizePhase(row["Fas"]) === "Muntlig överenskommelse";
        })
        .sort((a, b) => {
          const dateA = a["Avslutsdatum"] ? new Date(a["Avslutsdatum"]).getTime() : 0;
          const dateB = b["Avslutsdatum"] ? new Date(b["Avslutsdatum"]).getTime() : 0;
          return dateA - dateB;
        });
    },
    muntligTotalValue() {
      return this.muntligOpportunities.reduce((sum, opp, index) => {
        if (this.excludedMuntligOpportunities.has(index)) return sum;
        return sum + (Number(opp["Ordervärde"]) || 0);
      }, 0);
    },
    closingByDay() {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();
      const today = now.getDate();
      const lastDay = new Date(currentYear, currentMonth + 1, 0).getDate();

      const futureOpportunities = this.data.filter((row) => {
        const dateStr = row["Avslutsdatum"];
        if (!dateStr) return false;
        const date = new Date(dateStr);
        if (date.getFullYear() !== currentYear || date.getMonth() !== currentMonth) return false;
        return date.getDate() >= today;
      });

      const dayMap = {};
      futureOpportunities.forEach((row) => {
        const date = new Date(row["Avslutsdatum"]);
        const day = date.getDate();
        const phase = normalizePhase(row["Fas"]);
        const value = Number(row["Ordervärde"]) || 0;
        if (!dayMap[day]) dayMap[day] = {};
        dayMap[day][phase] = (dayMap[day][phase] || 0) + value;
      });

      const result = [];
      for (let day = today; day <= lastDay; day++) {
        const date = new Date(currentYear, currentMonth, day);
        const dayData = {
          date: date.toLocaleDateString("sv-SE", { weekday: "short", day: "numeric", month: "short" }),
        };
        PHASE_ORDER.forEach((phase) => {
          dayData[phase] = (dayMap[day] && dayMap[day][phase]) || 0;
        });
        result.push(dayData);
      }
      return result;
    },
    closingByDayTotal() {
      return this.closingByDay.reduce((sum, day) => {
        return sum + PHASE_ORDER.reduce((daySum, phase) => daySum + (day[phase] || 0), 0);
      }, 0);
    },
    closingByMonth() {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();
      const monthsData = [];

      for (let i = 0; i < 13; i++) {
        const monthDate = new Date(currentYear, currentMonth + i, 1);
        const year = monthDate.getFullYear();
        const month = monthDate.getMonth();

        const monthOpportunities = this.data.filter((row) => {
          const dateStr = row["Avslutsdatum"];
          if (!dateStr) return false;
          const date = new Date(dateStr);
          return date.getFullYear() === year && date.getMonth() === month;
        });

        const phaseValues = {};
        let totalValue = 0;
        monthOpportunities.forEach((row) => {
          const phase = normalizePhase(row["Fas"]);
          const value = Number(row["Ordervärde"]) || 0;
          phaseValues[phase] = (phaseValues[phase] || 0) + value;
          totalValue += value;
        });

        const monthData = {
          month: monthDate.toLocaleDateString("sv-SE", { month: "short", year: "numeric" }),
          antal: monthOpportunities.length,
          totalValue,
        };
        PHASE_ORDER.forEach((phase) => {
          monthData[phase] = phaseValues[phase] || 0;
        });
        monthsData.push(monthData);
      }
      return monthsData;
    },
    selectedSellerOpportunities() {
      if (!this.selectedSeller) return [];
      return this.currentMonthData
        .filter((row) => row["Säljare"] === this.selectedSeller)
        .sort((a, b) => {
          const dateA = a["Avslutsdatum"] ? new Date(a["Avslutsdatum"]).getTime() : 0;
          const dateB = b["Avslutsdatum"] ? new Date(b["Avslutsdatum"]).getTime() : 0;
          return dateA - dateB;
        });
    },
    selectedSellerTotalValue() {
      return this.selectedSellerOpportunities.reduce((sum, row) => sum + (Number(row["Ordervärde"]) || 0), 0);
    },
    selectedSellerAvgValue() {
      const len = this.selectedSellerOpportunities.length;
      return len > 0 ? this.selectedSellerTotalValue / len : 0;
    },
  },
  watch: {
    data: {
      handler() {
        this.excludedMuntligOpportunities = new Set();
        this.top10PhaseFilter = "Alla";
        this.$nextTick(() => this.renderCharts());
      },
      deep: true,
    },
    phaseFilterSeller() {
      this.$nextTick(() => this.renderPhaseTotalChart());
    },
  },
  mounted() {
    this.$nextTick(() => this.renderCharts());
  },
  beforeDestroy() {
    this.destroyCharts();
  },
  methods: {
    phaseColor(name) {
      return PHASE_COLORS[name] || "#94a3b8";
    },
    normalizePhase(phase) {
      return normalizePhase(phase);
    },
    toggleMuntligExclusion(index) {
      const newExcluded = new Set(this.excludedMuntligOpportunities);
      if (newExcluded.has(index)) {
        newExcluded.delete(index);
      } else {
        newExcluded.add(index);
      }
      this.excludedMuntligOpportunities = newExcluded;
    },
    formatNumber(num) {
      return num.toLocaleString("sv-SE", { maximumFractionDigits: 0 });
    },
    destroyCharts() {
      Object.values(this.charts).forEach((chart) => {
        if (chart) chart.destroy();
      });
      this.charts = {};
    },
    renderCharts() {
      this.destroyCharts();
      this.renderPhaseTotalChart();
      this.renderSellerValueChart();
      this.renderSellerCountChart();
      this.renderAverageValueChart();
      this.renderClosingDayChart();
      this.renderClosingMonthChart();
    },
    renderPhaseTotalChart() {
      if (this.charts.phaseTotalChart) {
        this.charts.phaseTotalChart.destroy();
        delete this.charts.phaseTotalChart;
      }
      if (this.filteredPhaseData.length === 0) return;
      const el = this.$refs.phaseTotalChart;
      if (!el) return;
      const labels = this.filteredPhaseData.map((d) => d.name);
      const phaseDataRef = this.filteredPhaseData;
      const datasets = this.filteredPhaseData.map((d, i) => ({
        label: d.name,
        data: this.filteredPhaseData.map((_, j) => j === i ? d.value : null),
        backgroundColor: PHASE_COLORS[d.name] || "#94a3b8",
      }));
      this.charts.phaseTotalChart = new Chart(el.getContext("2d"), {
        type: "bar",
        data: { labels, datasets },
        options: {
          responsive: true,
          scales: {
            xAxes: [{ stacked: true }],
            yAxes: [{ stacked: true, ticks: { callback: (v) => this.formatNumber(v) } }],
          },
          legend: {
            display: true,
            position: "top",
          },
          tooltips: {
            callbacks: {
              label: (item) => {
                const d = phaseDataRef[item.index];
                return `${d.name}: ${this.formatNumber(d.value)} kr`;
              },
              afterLabel: (item) => {
                const d = phaseDataRef[item.index];
                const avg = d.count > 0 ? d.value / d.count : 0;
                return [`Antal: ${d.count} st`, `Genomsnitt: ${this.formatNumber(avg)} kr`];
              },
            },
          },
          onClick: (evt, elements) => {
            if (elements.length > 0) {
              const index = elements[0]._index;
              this.selectedPhase = labels[index];
              this.$modal.show("phase-detail-modal");
            }
          },
        },
      });
    },
    createStackedBarChart(ref, labels, phaseDatasets, onClick) {
      const el = this.$refs[ref];
      if (!el) return;
      const datasets = PHASE_ORDER.map((phase) => ({
        label: phase,
        data: phaseDatasets.map((d) => d[phase] || 0),
        backgroundColor: PHASE_COLORS[phase] || "#94a3b8",
      }));
      this.charts[ref] = new Chart(el.getContext("2d"), {
        type: "bar",
        data: { labels, datasets },
        options: {
          responsive: true,
          scales: {
            xAxes: [{ stacked: true }],
            yAxes: [{ stacked: true, ticks: { callback: (v) => this.formatNumber(v) } }],
          },
          tooltips: {
            callbacks: {
              label: (item, data) => {
                const dsLabel = data.datasets[item.datasetIndex].label;
                return `${dsLabel}: ${this.formatNumber(item.yLabel)} kr`;
              },
            },
          },
          onClick: onClick
            ? (evt, elements) => {
                if (elements.length > 0) {
                  const index = elements[0]._index;
                  onClick(labels[index]);
                }
              }
            : undefined,
        },
      });
    },
    renderSellerValueChart() {
      if (this.sellerData.length === 0) return;
      const el = this.$refs.sellerValueChart;
      if (!el) return;
      const labels = this.sellerData.map((s) => s.name);
      const datasets = PHASE_ORDER.map((phase) => ({
        label: phase,
        data: this.sellerData.map((d) => d[phase] || 0),
        backgroundColor: PHASE_COLORS[phase] || "#94a3b8",
      }));
      const avg = this.sellerData.reduce((s, d) => s + d.total, 0) / this.sellerData.length;
      this.charts.sellerValueChart = new Chart(el.getContext("2d"), {
        type: "bar",
        data: { labels, datasets },
        options: {
          responsive: true,
          scales: {
            xAxes: [{ stacked: true }],
            yAxes: [{ stacked: true, ticks: { callback: (v) => this.formatNumber(v) } }],
          },
          tooltips: {
            callbacks: {
              label: (item, data) => {
                const dsLabel = data.datasets[item.datasetIndex].label;
                return `${dsLabel}: ${this.formatNumber(item.yLabel)} kr`;
              },
            },
          },
          annotation: {
            annotations: [
              {
                type: "line",
                mode: "horizontal",
                scaleID: "y-axis-0",
                value: avg,
                borderColor: "#F1A239",
                borderWidth: 2,
                borderDash: [5, 5],
                label: { enabled: true, content: `Genomsnitt: ${this.formatNumber(avg)} kr`, position: "left" },
              },
            ],
          },
          onClick: (evt, elements) => {
            if (elements.length > 0) {
              const index = elements[0]._index;
              this.selectedSeller = labels[index];
              this.$modal.show("seller-detail-modal");
            }
          },
        },
      });
    },
    renderSellerCountChart() {
      if (this.sellerOpportunityCount.length === 0) return;
      const labels = this.sellerOpportunityCount.map((s) => s.name);
      const el = this.$refs.sellerCountChart;
      if (!el) return;
      const datasets = PHASE_ORDER.map((phase) => ({
        label: phase,
        data: this.sellerOpportunityCount.map((d) => d[phase] || 0),
        backgroundColor: PHASE_COLORS[phase] || "#94a3b8",
      }));
      this.charts.sellerCountChart = new Chart(el.getContext("2d"), {
        type: "bar",
        data: { labels, datasets },
        options: {
          responsive: true,
          scales: {
            xAxes: [{ stacked: true }],
            yAxes: [{ stacked: true }],
          },
          tooltips: {
            callbacks: {
              label: (item, data) => {
                const dsLabel = data.datasets[item.datasetIndex].label;
                return `${dsLabel}: ${this.formatNumber(item.yLabel)} st`;
              },
            },
          },
        },
      });
    },
    renderPhaseChart() {
      if (this.phaseData.length === 0) return;
      const el = this.$refs.phaseChart;
      if (!el) return;
      const phaseColors = this.phaseData.map((p) => PHASE_COLORS[p.name] || "#94a3b8");
      this.charts.phaseChart = new Chart(el.getContext("2d"), {
        type: "pie",
        data: {
          labels: this.phaseData.map((p) => p.name),
          datasets: [{ data: this.phaseData.map((p) => p.value), backgroundColor: phaseColors }],
        },
        options: {
          responsive: true,
          tooltips: {
            callbacks: {
              label: (item, data) => {
                const label = data.labels[item.index];
                const value = data.datasets[0].data[item.index];
                const pd = this.phaseData[item.index];
                return `${label}: ${this.formatNumber(value)} kr (${pd.count} st)`;
              },
            },
          },
        },
      });
    },
    renderAverageValueChart() {
      if (this.sellerAverageValue.length === 0) return;
      const el = this.$refs.averageValueChart;
      if (!el) return;
      const labels = this.sellerAverageValue.map((s) => s.name);
      const values = this.sellerAverageValue.map((s) => s.genomsnitt);
      const avg = values.reduce((s, v) => s + v, 0) / values.length;
      this.charts.averageValueChart = new Chart(el.getContext("2d"), {
        type: "bar",
        data: {
          labels,
          datasets: [
            { label: "Genomsnitt (kr)", data: values, backgroundColor: "#84ABEA" },
          ],
        },
        options: {
          responsive: true,
          scales: {
            yAxes: [{ ticks: { callback: (v) => this.formatNumber(v) } }],
          },
          tooltips: {
            callbacks: {
              label: (item) => `${this.formatNumber(item.yLabel)} kr`,
            },
          },
          annotation: {
            annotations: [
              {
                type: "line",
                mode: "horizontal",
                scaleID: "y-axis-0",
                value: avg,
                borderColor: "#F1A239",
                borderWidth: 2,
                borderDash: [5, 5],
                label: { enabled: true, content: `Genomsnitt: ${this.formatNumber(avg)} kr`, position: "left" },
              },
            ],
          },
          onClick: (evt, elements) => {
            if (elements.length > 0) {
              const index = elements[0]._index;
              this.selectedSeller = labels[index];
              this.$modal.show("seller-detail-modal");
            }
          },
        },
      });
    },
    renderClosingDayChart() {
      if (this.closingByDay.length === 0) return;
      const el = this.$refs.closingDayChart;
      if (!el) return;
      const labels = this.closingByDay.map((d) => d.date);
      const datasets = PHASE_ORDER.map((phase) => ({
        label: phase,
        data: this.closingByDay.map((d) => d[phase] || 0),
        backgroundColor: PHASE_COLORS[phase] || "#94a3b8",
      }));
      this.charts.closingDayChart = new Chart(el.getContext("2d"), {
        type: "bar",
        data: { labels, datasets },
        options: {
          responsive: true,
          scales: {
            xAxes: [{ stacked: true }],
            yAxes: [{ stacked: true, ticks: { callback: (v) => this.formatNumber(v) } }],
          },
          tooltips: {
            callbacks: {
              label: (item, data) => {
                const dsLabel = data.datasets[item.datasetIndex].label;
                return `${dsLabel}: ${this.formatNumber(item.yLabel)} kr`;
              },
            },
          },
        },
      });
    },
    renderClosingMonthChart() {
      if (this.closingByMonth.length === 0) return;
      const el = this.$refs.closingMonthChart;
      if (!el) return;
      const labels = this.closingByMonth.map((m) => m.month);
      const datasets = PHASE_ORDER.map((phase) => ({
        label: phase,
        data: this.closingByMonth.map((m) => m[phase] || 0),
        backgroundColor: PHASE_COLORS[phase] || "#94a3b8",
      }));
      this.charts.closingMonthChart = new Chart(el.getContext("2d"), {
        type: "bar",
        data: { labels, datasets },
        options: {
          responsive: true,
          scales: {
            xAxes: [{ stacked: true }],
            yAxes: [{ stacked: true, ticks: { callback: (v) => this.formatNumber(v) } }],
          },
          tooltips: {
            callbacks: {
              label: (item, data) => {
                const dsLabel = data.datasets[item.datasetIndex].label;
                return `${dsLabel}: ${this.formatNumber(item.yLabel)} kr`;
              },
            },
          },
        },
      });
    },
  },
};
</script>
